export const INBOUND_ARRIVAL_UNIT_KINDS = [
  "carton",
  "crate",
  "container",
  "box",
  "bag",
  "pallet",
  "reel",
  "tray",
  "each",
] as const;

export type InboundArrivalUnitKind = (typeof INBOUND_ARRIVAL_UNIT_KINDS)[number];

export const DEFAULT_INBOUND_ARRIVAL_UNIT_KIND: InboundArrivalUnitKind = "container";

/** Default for sealed put-away containers when the API omits unit_kind (legacy rows). */
export const DEFAULT_INBOUND_SUBCONTAINER_UNIT_KIND: InboundArrivalUnitKind = "box";

export function parseInboundArrivalUnitKind(value: unknown): InboundArrivalUnitKind {
  if (value === "package") return "container";
  if (
    typeof value === "string"
    && (INBOUND_ARRIVAL_UNIT_KINDS as readonly string[]).includes(value)
  ) {
    return value as InboundArrivalUnitKind;
  }
  return DEFAULT_INBOUND_ARRIVAL_UNIT_KIND;
}

export function parseInboundSubcontainerUnitKind(value: unknown): InboundArrivalUnitKind {
  if (value === "package") return "container";
  if (
    typeof value === "string"
    && (INBOUND_ARRIVAL_UNIT_KINDS as readonly string[]).includes(value)
  ) {
    return value as InboundArrivalUnitKind;
  }
  return DEFAULT_INBOUND_SUBCONTAINER_UNIT_KIND;
}

export function inboundArrivalUnitKindLabel(kind: InboundArrivalUnitKind, count = 1): string {
  if (count === 1) return kind;
  if (kind === "box") return "boxes";
  return `${kind}s`;
}

export function formatDockShipmentUnitCount(
  unitCount: number,
  units: readonly { unit_kind: InboundArrivalUnitKind }[],
): string {
  if (unitCount <= 0) return "";
  if (units.length === 0) {
    return `${String(unitCount)} ${inboundArrivalUnitKindLabel(DEFAULT_INBOUND_ARRIVAL_UNIT_KIND, unitCount)}`;
  }
  const kinds = [...new Set(units.map((unit) => unit.unit_kind))];
  if (kinds.length === 1) {
    return `${String(unitCount)} ${inboundArrivalUnitKindLabel(kinds[0]!, unitCount)}`;
  }
  return `${String(unitCount)} units`;
}

export function inboundArrivalManageUnitsHint(
  units: readonly { unit_kind: InboundArrivalUnitKind }[],
): string {
  if (units.length === 0) {
    return `Tap to manage ${inboundArrivalUnitKindLabel(DEFAULT_INBOUND_ARRIVAL_UNIT_KIND, 2)}`;
  }
  const kinds = [...new Set(units.map((unit) => unit.unit_kind))];
  if (kinds.length === 1) {
    return `Tap to manage ${inboundArrivalUnitKindLabel(kinds[0]!, 2)}`;
  }
  return "Tap to manage units";
}

export function formatDockShipmentLoggedSummary(
  unitCount: number,
  units: readonly { unit_kind: InboundArrivalUnitKind }[],
): string {
  if (unitCount <= 0) return "No units logged yet";
  const countLabel = formatDockShipmentUnitCount(unitCount, units);
  return `${countLabel} logged`;
}

export function formatInboundArrivalUnitHeading(
  kind: InboundArrivalUnitKind,
  packageNo: number,
): string {
  const label = inboundArrivalUnitKindLabel(kind, 1);
  const capitalized = label.charAt(0).toUpperCase() + label.slice(1);
  return `${capitalized} ${String(packageNo)}`;
}

export function inboundReceiveOrderLabel(input: {
  sourceNumber: string | null | undefined;
  sourceId: string;
}): string {
  if (input.sourceNumber != null && String(input.sourceNumber).trim() !== "") {
    return String(input.sourceNumber).trim();
  }
  return input.sourceId;
}

export function inboundReceiveFocusedTitle(input: {
  sourceNumber: string | null | undefined;
  sourceId: string;
}): string {
  return `Receive ${inboundReceiveOrderLabel(input)}`;
}

export const INBOUND_ARRIVAL_UNIT_KIND_OPTIONS: ReadonlyArray<{
  value: InboundArrivalUnitKind;
  label: string;
}> = [
  { value: "carton", label: "Carton" },
  { value: "crate", label: "Crate" },
  { value: "container", label: "Container (ambiguous)" },
  { value: "box", label: "Box" },
  { value: "bag", label: "Bag" },
  { value: "pallet", label: "Pallet" },
  { value: "reel", label: "Reel" },
  { value: "tray", label: "Tray" },
  { value: "each", label: "Each" },
];

/** Prefer the unit type already on this SKU. Mixed or unused stock stays Container. */
export function defaultCarveContainerUnitKind(
  unitKindsInUse: readonly string[],
): InboundArrivalUnitKind {
  const counts = new Map<InboundArrivalUnitKind, number>();
  for (const raw of unitKindsInUse) {
    const kind = raw.trim().toLowerCase();
    if (kind === "container") continue;
    if (!(INBOUND_ARRIVAL_UNIT_KINDS as readonly string[]).includes(kind)) continue;
    const typed = kind as InboundArrivalUnitKind;
    counts.set(typed, (counts.get(typed) ?? 0) + 1);
  }
  let bestKind: InboundArrivalUnitKind | null = null;
  let bestCount = 0;
  let tied = false;
  for (const [kind, count] of counts) {
    if (count > bestCount) {
      bestKind = kind;
      bestCount = count;
      tied = false;
    } else if (count === bestCount) {
      tied = true;
    }
  }
  if (bestKind == null || tied) return "container";
  return bestKind;
}

export function inboundArrivalUnitKindTileLabel(kind: InboundArrivalUnitKind): string {
  const option = INBOUND_ARRIVAL_UNIT_KIND_OPTIONS.find((row) => row.value === kind);
  if (option == null) return "Box";
  if (kind === "container") return "Container";
  if (kind === "each") return "Each";
  return option.label;
}

/** Receive put-away type tiles — alphabetical by visible label (Bag, Box, Carton, …). */
export const INBOUND_SUBCONTAINER_UNIT_KIND_TILE_KINDS: readonly InboundArrivalUnitKind[] = [
  ...INBOUND_ARRIVAL_UNIT_KINDS,
].sort((left, right) =>
  inboundArrivalUnitKindTileLabel(left).localeCompare(inboundArrivalUnitKindTileLabel(right), undefined, {
    sensitivity: "base",
  }),
);

export function resolveOpenSubcontainerUnitKindFromReceipt(stored: string | null | undefined): {
  kind: InboundArrivalUnitKind | null;
  userSet: boolean;
} {
  if (stored == null || stored.trim() === "") {
    return { kind: null, userSet: false };
  }
  return { kind: parseInboundArrivalUnitKind(stored), userSet: true };
}

export function formatOpenSubcontainerHeading(
  nextContainerNo: number,
  kind: InboundArrivalUnitKind | null,
): string {
  if (kind == null) {
    return `Container ${String(nextContainerNo)}`;
  }
  return formatInboundSubcontainerHeading(kind, nextContainerNo);
}

export function formatInboundSubcontainerHeading(
  kind: InboundArrivalUnitKind,
  subcontainerNo: number,
): string {
  return formatInboundArrivalUnitHeading(kind, subcontainerNo);
}
